import React from 'react'
import {
  Alert, Box, Card, CardContent, Chip, Divider, Grid, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material'
import PageHeader from '../components/ui/PageHeader'

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>{title}</Typography>
    {children}
  </Box>
)

const Rows: React.FC<{ head: string[]; rows: (string | React.ReactNode)[][] }> = ({ head, rows }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>{head.map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}>{r.map((c, j) => <TableCell key={j} sx={{ verticalAlign: 'top' }}>{c}</TableCell>)}</TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

const ShippingHelpPage: React.FC = () => (
  <Box sx={{ maxWidth: 1000 }}>
    <PageHeader
      title="Shipping Help"
      subtitle="How to book, track and troubleshoot shipments with Delhivery and Shiprocket. Updated 14 Oct 2026."
      breadcrumbs={[{ label: 'Sales' }, { label: 'Shipments', path: '/admin/shipments' }, { label: 'Help' }]}
    />

    <Card>
      <CardContent sx={{ p: { xs: 2, md: 4 } }}>
        <Section title="Our carriers">
          <Rows
            head={['Carrier', 'Use it for', 'Login type', 'Status']}
            rows={[
              ['Delhivery B2C', 'Parcels to businesses and customers (the default)', 'One API token (Delhivery One → Settings → API Setup)', <Chip key="a" size="small" color="success" label="In use" />],
              ['Shiprocket', 'Multi-courier booking (Delhivery, Blue Dart, Ekart and others)', 'API user email + password (not your panel login)', <Chip key="b" size="small" color="success" label="In use" />],
              ['Delhivery B2B (freight)', 'Heavy or pallet freight with a Lorry Receipt (LR)', 'Separate username + password with its own API', <Chip key="c" size="small" label="Not switched on yet" />],
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The B2B account is separate from B2C. The system has a B2B connector, but it is not enabled and needs B2B API
            credentials plus Delhivery&apos;s B2B API document to finish and test. Ask your Delhivery account manager for both.
          </Typography>
        </Section>

        <Section title="Book a shipment">
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li><Typography variant="body2">Open <b>Sales → Shipments</b>, find the order (it must be <b>Confirmed</b> or <b>Processing</b>) and use the create-shipment action on that order.</Typography></li>
            <li><Typography variant="body2">Pick the carrier. For Delhivery choose the warehouse; for Shiprocket choose the pickup location from the list (it shows only locations that really exist in your Shiprocket account).</Typography></li>
            <li><Typography variant="body2">Weight is in <b>kg</b> and size in <b>cm</b>. Leave weight blank to add up the item weights (variant weight, then product weight, then 0.5 kg per item).</Typography></li>
            <li><Typography variant="body2">After booking, the message shows the weight and size that were sent. Check them against the label.</Typography></li>
          </ol>
        </Section>

        <Section title="Units and rules that matter">
          <Rows
            head={['Item', 'Rule']}
            rows={[
              ['Weight', 'Enter kg. The system converts to grams for Delhivery (2.5 kg is sent as 2500). Shiprocket takes kg directly. Zero or empty weight is refused.'],
              ['Size', 'Length, width and height in cm, whole numbers. Defaults are 30 × 20 × 15 if left unchanged. Zero is refused.'],
              ['Warehouse / pickup name', 'Must match the name registered with the carrier exactly, including capitals and spaces. Delhivery: NOIDA, NIMIYA. Shiprocket: Office, work, GAYA STA.'],
              ['Order number', 'Must be unique for each shipment at the carrier.'],
              ['Special characters', 'Avoid & # % ; \\ in names and addresses. Delhivery rejects them in the order data.'],
              ['Payment', 'Shipments are booked as Prepaid. COD booking is not enabled in the Shipments screen.'],
            ]}
          />
        </Section>

        <Section title="Track, label, pickup, cancel">
          <Rows
            head={['Task', 'Where / how', 'Good to know']}
            rows={[
              ['Track one order', 'Shipments list → the Track Shipment (refresh) icon on the order row', 'Shows status and scan history from the carrier.'],
              ['Track any AWB', 'Shipments → Track Direct / External AWB (also works for parcels booked directly in the carrier panel)', 'A failed lookup shows an error but no longer changes the order\'s stored status.'],
              ['Label', 'Shipments list → the Download Label icon on the order row', 'Delhivery labels print on A4. If you need 4×6 inch labels for a label printer, download them from the Delhivery panel or ask IT to add a size option.'],
              ['Pickup request', 'Raised per warehouse, not per parcel', 'One request covers all parcels from that warehouse that day. A second one can be raised only after the first is closed. You can also do this in the carrier panel.'],
              ['Cancel (Delhivery)', 'Carrier panel for now', 'Allowed only while Manifested, In Transit or Pending. Cancelling a manifested parcel before pickup keeps it as Manifested/Undelivered.'],
            ]}
          />
        </Section>

        <Section title="When something goes wrong">
          <Rows
            head={['You see', 'Usually means', 'What to do']}
            rows={[
              ['401 "Login or API Key Required" / "Invalid API key" (Delhivery)', 'The token was regenerated (the old one stops working immediately), or the token is not valid from our server.', 'In Delhivery One → Settings → API Setup, confirm the token. If it was regenerated, paste the new one into Integrations Hub → Delhivery. Tell IT if it still fails.'],
              ['Tracking fails but booking worked', 'Same as above, or the parcel is too new to have scans.', 'Retry after a few minutes; if it persists treat it as a token problem.'],
              ['"Wrong pickup location" (Shiprocket)', 'The pickup name is not in your Shiprocket account.', 'Choose one from the list in the dialog; add new ones in the Shiprocket panel.'],
              ['"Phone number is in invalid format"', 'Customer phone is not a valid 10-digit Indian number.', 'Fix the phone on the customer or address, then rebook.'],
              ['Pincode not serviceable', 'Delhivery returned no service for that pincode (or it is under an embargo).', 'Try Shiprocket, which has more couriers, or contact the customer.'],
              ['Shipment shows zero weight', 'Older bookings sent kg as if it were grams.', 'Fixed for new bookings. Correct old ones in the carrier panel if billing matters.'],
              ['Too many requests', 'Carriers limit calls per IP (for example 750 tracking calls per 5 minutes).', 'Wait a few minutes.'],
            ]}
          />
        </Section>

        <Section title="Keys and safety">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li><Typography variant="body2">Carrier keys live in <b>Integrations & IT → Integrations Hub</b>. Only admins with integration access can see or change them. Never send keys by email or chat.</Typography></li>
            <li><Typography variant="body2">Delhivery shows a newly generated token for only 5 minutes, then hides it. Copy it straight into the Integrations Hub. Generating a new one cuts off the old one immediately, so do it when no one is booking.</Typography></li>
            <li><Typography variant="body2">Use the Delhivery <b>live</b> token for the live site. Staging tokens and staging addresses are separate and do not work in production.</Typography></li>
          </ul>
        </Section>

        <Alert severity="info" sx={{ mb: 3 }}>
          Before the first live shipment of the day: check the pickup name, weight and size on the dialog, and that the
          customer&apos;s phone and pincode look right. Most failures come from these four.
        </Alert>

        <Divider sx={{ mb: 2 }} />
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Link href="https://one.delhivery.com/developer-portal/document/b2c/detail/order-creation" target="_blank" rel="noopener noreferrer">Delhivery shipment creation</Link>
          <Link href="https://one.delhivery.com/developer-portal/document/b2c/detail/order-tracking" target="_blank" rel="noopener noreferrer">Delhivery tracking</Link>
          <Link href="https://help.delhivery.com/docs/api-token-generation" target="_blank" rel="noopener noreferrer">Delhivery API token</Link>
          <Link href="https://apidocs.shiprocket.in" target="_blank" rel="noopener noreferrer">Shiprocket API docs</Link>
        </Stack>
        <Grid container sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Based on Delhivery&apos;s B2C developer documentation and our live checks on 14 Oct 2026. Delhivery B2B details to be added once the B2B API document is available.
          </Typography>
        </Grid>
      </CardContent>
    </Card>
  </Box>
)

export default ShippingHelpPage
